import React, { useEffect, useMemo, useRef, useState } from "react";
import COUNTRIES, {
  buildE164,
  normalizeLocalDigits,
  parseE164,
} from "./countries";
import "./phonefield.css";

/**
 * International phone field with a searchable country selector.
 *
 * Props:
 *   - value: the stored E.164 value ("+213555123456") OR the national digits
 *   - onChange: called with the new E.164 value (or "" when empty)
 *   - onValidChange?: called with a boolean whenever the validity changes
 *   - defaultIso?: default country when value is empty. Defaults to "DZ".
 *   - name, placeholder, required, disabled, className, inputClassName
 */
export default function PhoneField({
  value,
  onChange,
  onValidChange,
  defaultIso = "DZ",
  name = "phone",
  placeholder,
  required,
  disabled,
  className = "",
  inputClassName = "",
}) {
  const [country, setCountry] = useState(() => {
    if (typeof value === "string" && value.startsWith("+")) return parseE164(value).country;
    return COUNTRIES.find((c) => c.iso === defaultIso) || COUNTRIES[0];
  });
  const [digits, setDigits] = useState(() => {
    if (typeof value === "string" && value.startsWith("+")) return parseE164(value).digits;
    return normalizeLocalDigits(value);
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  // Keep state in sync when parent supplies an E.164 value
  useEffect(() => {
    if (typeof value === "string" && value.startsWith("+")) {
      const parsed = parseE164(value);
      setCountry(parsed.country);
      setDigits(parsed.digits);
    } else if (!value) {
      setDigits("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const isValid = useMemo(() => {
    if (!digits) return false;
    return country.pattern.test(digits);
  }, [country, digits]);

  useEffect(() => {
    if (typeof onValidChange === "function") onValidChange(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const emit = (nextCountry, nextDigits) => {
    if (!nextDigits) {
      onChange?.("");
      return;
    }
    onChange?.(buildE164(nextCountry, nextDigits));
  };

  const handleSelectCountry = (c) => {
    setCountry(c);
    setOpen(false);
    setSearch("");
    emit(c, digits);
  };

  const handleDigitsChange = (e) => {
    const next = normalizeLocalDigits(e.target.value);
    setDigits(next);
    emit(country, next);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return COUNTRIES;
    const q = search.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        String(c.code).includes(q.replace(/^\+/, "")) ||
        c.iso.toLowerCase().includes(q)
    );
  }, [search]);

  const dialCode = String(country.code).split("-")[0];
  const invalid = digits.length > 0 && !isValid;

  return (
    <div className={`pf-root ${className}`} ref={wrapperRef}>
      <div className={`pf-shell ${invalid ? "pf-shell--error" : ""} ${disabled ? "pf-shell--disabled" : ""}`}>
        <button
          type="button"
          className="pf-country"
          onClick={() => !disabled && setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
        >
          <span className="pf-flag" aria-hidden="true">{country.flag}</span>
          <span className="pf-code">+{dialCode}</span>
          <span className="pf-caret" aria-hidden="true">▾</span>
        </button>
        <input
          type="tel"
          name={name}
          className={`pf-input ${inputClassName}`}
          value={digits}
          onChange={handleDigitsChange}
          placeholder={placeholder || country.placeholder}
          required={required}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="tel-national"
        />
      </div>

      {open && !disabled && (
        <div className="pf-dropdown" role="listbox">
          <div className="pf-search">
            <input
              autoFocus
              type="text"
              placeholder="Search country or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="pf-list">
            {filtered.length === 0 && (
              <li className="pf-empty">No country matches “{search}”.</li>
            )}
            {filtered.map((c) => (
              <li
                key={c.iso}
                role="option"
                aria-selected={c.iso === country.iso}
                className={`pf-item ${c.iso === country.iso ? "is-active" : ""}`}
                onClick={() => handleSelectCountry(c)}
              >
                <span className="pf-item-flag">{c.flag}</span>
                <span className="pf-item-name">{c.name}</span>
                <span className="pf-item-code">+{String(c.code).split("-")[0]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {invalid && (
        <p className="pf-error">
          Invalid number for {country.name} (expected {country.placeholder}).
        </p>
      )}
    </div>
  );
}
